import { lazy } from 'react'
import { AccountTypeGuard } from '@/components/auth/AccountTypeGuard'
import { SubscriptionFeatureGuard } from '@/components/auth/SubscriptionFeatureGuard'
import { OrgRouteGuard } from '@/components/organization/OrgRouteGuard'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

import {
  AccountType,
  IntegrationType,
  isIntegrationVisible,
} from '@scrambl/shared'

import { ProtectedLayout } from '../components'
import { AppRouteObject } from '../types'

// Administration pages are hosted by the Settings shell
// (/settings/administration/<page>, see pages/settings-hub/lib/admin-hosts.ts)
// and the former admin mode is gone. What remains under /admin: redirects
// for old links, bookmarks and Help Center articles, plus the pages that are
// return targets for external flows (QuickBooks OAuth, Stripe checkout) and
// the design standard reference.
const QuickBooksIntegrationErrorPage = lazy(
  () => import('@/pages/admin/integrations/quickbooks-error'),
)
const QuickBooksIntegrationSuccessPage = lazy(
  () => import('@/pages/admin/integrations/quickbooks-success'),
)
const AdminSubscriptionSetupSuccessPage = lazy(
  () => import('@/pages/admin/subscription/setup-success'),
)
const AdminSubscriptionSuccessPage = lazy(
  () => import('@/pages/admin/subscription/success'),
)
const AdminDesignStandardPage = lazy(() => import('@/pages/admin/design'))

/**
 * /admin/<old> → /:orgSlug/<to>. Relative `../` hops can't reach the org root
 * from here: '/:orgSlug/admin' is a single route, so climbing past it lands on
 * '/' and drops the org slug.
 */
function LegacyAdminOrgRedirect({ to }: { to: string }) {
  const { orgSlug } = useParams()
  return <Navigate to={`/${orgSlug}/${to}`} replace />
}

export function LegacyAdminTemplatesRedirect({
  destination = 'statuses',
}: {
  destination?: 'statuses' | 'project-templates'
}) {
  const { orgSlug } = useParams()
  const target =
    destination === 'project-templates'
      ? `/${orgSlug}/projects/templates`
      : `/${orgSlug}/projects/settings?section=statuses`

  return <Navigate to={target} replace />
}

/**
 * /admin/<page> → the same page inside the Settings shell, keeping the
 * query and hash (Organization Settings reads `?section=`).
 */
export function LegacyAdminSettingsRedirect({ page }: { page?: string }) {
  const { orgSlug } = useParams()
  const location = useLocation()
  const pathname = page
    ? `/${orgSlug}/settings/administration/${page}`
    : `/${orgSlug}/settings`
  // Plain /settings opens account settings; Organization Settings needs a
  // section, so an old bare /admin/settings link lands on General.
  const search =
    !page && !location.search ? '?section=general' : location.search

  return <Navigate to={{ pathname, search, hash: location.hash }} replace />
}

// Admin routes configuration
export const adminRoutes: AppRouteObject[] = [
  {
    path: '/:orgSlug/admin',
    element: <ProtectedLayout />,
    children: [
      {
        element: <OrgRouteGuard />,
        children: [
          {
            index: true,
            element: <LegacyAdminSettingsRedirect />,
          },
          {
            path: 'dashboard',
            element: <LegacyAdminSettingsRedirect />,
            title: 'Settings',
          },
          {
            path: 'settings',
            element: <LegacyAdminSettingsRedirect />,
            title: 'Settings',
          },
          {
            path: 'members',
            element: <LegacyAdminSettingsRedirect page="members" />,
            title: 'Members',
          },
          {
            path: 'permissions',
            element: <LegacyAdminSettingsRedirect page="members/permissions" />,
            title: 'Permissions',
          },
          {
            path: 'external-organizations',
            element: (
              <LegacyAdminSettingsRedirect page="members/external-organizations" />
            ),
            title: 'External organizations',
          },
          // Living reference for the launch design audit standard (Session K).
          {
            path: 'design',
            element: <AdminDesignStandardPage />,
            title: 'Design standard',
          },
          // Status configuration now lives in Project Settings. Keep redirects
          // for old Help Center links and saved bookmarks, but do not render
          // the retired admin templates experience.
          {
            path: 'templates',
            children: [
              {
                index: true,
                element: <LegacyAdminTemplatesRedirect />,
                title: 'Project settings',
              },
              {
                path: 'status-templates',
                element: <LegacyAdminTemplatesRedirect />,
                title: 'Project settings',
              },
              {
                path: 'project-templates',
                element: (
                  <LegacyAdminTemplatesRedirect destination="project-templates" />
                ),
                title: 'Project templates',
              },
            ],
          },
          {
            path: 'integrations',
            element: <LegacyAdminSettingsRedirect page="integrations" />,
            title: 'Integrations',
          },
          {
            path: 'integrations/quickbooks',
            element: (
              <LegacyAdminSettingsRedirect page="integrations/quickbooks" />
            ),
            title: 'QuickBooks settings',
          },
          // OAuth return targets — the API sends the browser here.
          {
            path: 'integrations/quickbooks/success',
            element: isIntegrationVisible(IntegrationType.QUICKBOOKS) ? (
              <SubscriptionFeatureGuard featureKey="quickbooks_online">
                <QuickBooksIntegrationSuccessPage />
              </SubscriptionFeatureGuard>
            ) : (
              <LegacyAdminSettingsRedirect page="integrations" />
            ),
            title: 'QuickBooks connected',
          },
          {
            path: 'integrations/quickbooks/error',
            element: isIntegrationVisible(IntegrationType.QUICKBOOKS) ? (
              <SubscriptionFeatureGuard featureKey="quickbooks_online">
                <QuickBooksIntegrationErrorPage />
              </SubscriptionFeatureGuard>
            ) : (
              <LegacyAdminSettingsRedirect page="integrations" />
            ),
            title: 'QuickBooks error',
          },
          {
            path: 'integrations/mcp',
            element: (
              <LegacyAdminOrgRedirect to="settings/personal#integrations" />
            ),
            title: 'MCP integration',
          },
          // Legacy /admin/time/* → /timesheets/* (grace period: ≥ 2026-10-21)
          {
            path: 'time',
            children: [
              {
                index: true,
                element: <LegacyAdminOrgRedirect to="timesheets" />,
              },
              {
                path: 'pending',
                element: <LegacyAdminOrgRedirect to="timesheets" />,
              },
              {
                path: 'team',
                element: <LegacyAdminOrgRedirect to="timesheets" />,
              },
              {
                path: 'settings',
                element: <LegacyAdminOrgRedirect to="timesheets/settings" />,
              },
            ],
          },
          // Subscription & billing — personal accounts are free by
          // construction (no SUBSCRIPTION flag row), so block the whole billing
          // surface by account type rather than relying on a feature flag.
          {
            path: 'subscription',
            element: (
              <AccountTypeGuard allowed={[AccountType.ORGANIZATION]}>
                <Outlet />
              </AccountTypeGuard>
            ),
            children: [
              {
                index: true,
                element: <LegacyAdminSettingsRedirect page="subscription" />,
                title: 'Subscription & billing',
              },
              {
                path: 'usage',
                element: (
                  <LegacyAdminSettingsRedirect page="subscription/usage" />
                ),
                title: 'Subscription usage',
              },
              {
                path: 'plans',
                element: (
                  <LegacyAdminSettingsRedirect page="subscription/plans" />
                ),
                title: 'Subscription plans',
              },
              // Stripe return targets.
              {
                path: 'success',
                element: <AdminSubscriptionSuccessPage />,
                title: 'Payment success',
              },
              {
                path: 'setup-success',
                element: <AdminSubscriptionSetupSuccessPage />,
                title: 'Setup success',
              },
            ],
          },
          {
            path: 'logs',
            element: <LegacyAdminSettingsRedirect page="logs" />,
            title: 'Audit trail',
          },
          {
            path: 'archive',
            element: <LegacyAdminSettingsRedirect page="archive" />,
            title: 'Archive',
          },
          {
            path: 'data',
            element: <LegacyAdminSettingsRedirect page="data" />,
            title: 'Data',
          },
        ],
      },
    ],
  },
]
